import { useSearchParams } from 'react-router-dom'
import { useState } from 'react'

import { User } from '@/types/User'

import { SubscriptionAwareProductFlow } from './SubscriptionAwareProductFlow'
import { PartnerMisconfigurationError, ErrorFallbackComponent } from '../ExternalPremiumLogin/ErrorComponents'
import { PremiumPaymentSession } from '../ExternalPremiumLogin/PremiumPaymentSession'
import RedeemLoginForm from '@/pages/external/redeem/RedeemLoginForm.tsx'

interface ExternalRedeemModalProps {
  token?: string | null
  base64ClientKey?: string | null
  promotionId?: string | null
  promotionIds?: string[] | null
  user?: User | null
}

/**
 * Main external redeem modal component that orchestrates the login and product selection flow
 * Handles the transition between login and product selection states
 */
export const ExternalRedeemModal: React.FC<ExternalRedeemModalProps> = ({
  token,
  base64ClientKey,
  promotionId,
  promotionIds,
  user,
}) => {
  const [searchParams] = useSearchParams()
  const [localToken, setLocalToken] = useState(token)
  const [localUser, setLocalUser] = useState<User | null>(user ?? null)

  const handleLoginSuccess = (token: string, user: User) => {
    setLocalToken(token)
    setLocalUser(user)
  }

  // Get parameters from props or search params with proper fallbacks
  const clientKey = base64ClientKey || searchParams.get('clientKey')
  const singlePromotion = promotionId || searchParams.get('promotionId')

  // Use promotionIds array if provided, otherwise wrap single promotionId in array
  const promotions = promotionIds && promotionIds.length > 0
    ? promotionIds
    : singlePromotion
      ? [singlePromotion]
      : null

  // Check for required parameters - both clientKey and promotionId are required
  if (!clientKey) {
    return (
      <ErrorFallbackComponent
        error={new PartnerMisconfigurationError('Missing required clientKey parameter')}
      />
    )
  }

  if (!promotions || promotions.length === 0) {
    return (
      <ErrorFallbackComponent
        error={new PartnerMisconfigurationError('Missing required promotionId parameter')}
      />
    )
  }

  // 1. Show subscription-aware product selection flow if user is authenticated
  if (localToken && localUser) {
    return (
      <PremiumPaymentSession token={localToken} base64ClientKey={clientKey} user={localUser}>
        <SubscriptionAwareProductFlow
          token={localToken}
          base64ClientKey={clientKey}
          promotionIds={promotions}
        />
      </PremiumPaymentSession>
    )
  }

  // 3. Show login form if not authenticated
  return (
    <RedeemLoginForm
      onLoginSuccess={handleLoginSuccess}
      clientKey={clientKey}
    />
  )
}

export default ExternalRedeemModal
